import React from 'react';
import { Trans } from 'react-i18next';

import ModalToBody from '../../../base/ModalToBody';
import { useEventMaterials } from '../../../hooks/api/event-material/useEventMaterials';
import { useUpdateMaterialRequest } from '../../../hooks/api/material-requests/useUpdateMaterialRequest';
import {
  EventMaterial,
  MaterialRequest,
  MaterialRequestDay,
  MaterialRequestQuantityPerDay,
} from '../../../hooks/api/utils/types';
import { defaultDayFormater } from '../../../libs/utils';

export type EditMaterialRequestDaysButtonProps = {
  request: MaterialRequest;
  admin?: boolean;
};

export const EditMaterialRequestDaysButton = ({ request, admin }: EditMaterialRequestDaysButtonProps) => {
  const [showModal, setShowModal] = React.useState(false);

  const { mutate: updateRequest } = useUpdateMaterialRequest(request.registrationId!);

  const { data: eventMaterials } = useEventMaterials(request?.registration?.eventId!, request.materialId!, {
    enabled: showModal,
  });

  const doUpdateRequest = (day: string, newAmount: number) => {
    // Since the endpoint at the API end is a destructive one (destroys and recreates all days),
    // we need to make sure we provide all relevant data to the endpoint.
    return updateRequest({
      request: {
        id: request.id!,
        materialId: request.materialId,
        material: request.material!,
        quantityPerDay: daystoQuantityPerDay(request.days!).map((d) => {
          if (d.day === day) {
            return {
              day: d.day,
              quantity: newAmount,
            };
          }
          return d;
        }),
      },
    });
  };

  return (
    <>
      <span key="button">
        <button
          className="btn btn-outline-accent m-btn btn-sm  m-btn--icon m--margin-left-5"
          onClick={() => setShowModal(true)}
        >
          <span>
            <i className="la la-map-marker" />
            <span>Amounts</span>
          </span>
        </button>
      </span>

      <ModalToBody key="modal" render={showModal}>
        <div
          className="modal fade show"
          id="m_modal_4"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="exampleModalLabel"
          style={{
            display: showModal ? 'block' : 'none',
            zIndex: 1039,
          }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  <Trans>Amounts</Trans>
                </h5>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={() => setShowModal(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.days?.map((day) => {
                      // we manually calculate a maximum _and_ add the already existing amount back to the
                      // total, since the API already considers pending requests and we want to make sure our
                      // 'red flag' for exceeding capacity doesn't raise false positives.

                      const max = getMaximum(day.day, eventMaterials?.data, day.quantity);
                      return (
                        <tr key={day.id}>
                          <td>
                            {defaultDayFormater(day.day)}&nbsp;
                            {admin ? (
                              <span style={max < day.quantity ? { color: 'red' } : {}}>({max} remaining)</span>
                            ) : null}
                          </td>
                          <td>
                            {admin ? (
                              <input
                                type="number"
                                className="form-control m-input"
                                defaultValue={day.quantity}
                                onBlur={(e) => doUpdateRequest(day.day, Number(e.target.value))}
                              />
                            ) : (
                              day.quantity
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div
          className={'modal-backdrop fade ' + (showModal ? ' show' : '')}
          style={{
            display: showModal ? 'block' : 'none',
            zIndex: 1038,
          }}
        />
      </ModalToBody>
    </>
  );
};

function daystoQuantityPerDay(days: MaterialRequestDay[]): MaterialRequestQuantityPerDay[] {
  return days.map((day) => ({
    day: day.day,
    quantity: day.quantity,
  }));
}

function getMaximum(day: string, eventMaterials: EventMaterial[] | undefined, quantity: number): number {
  const em = eventMaterials?.find((em) => em.day === day);
  if (!em) {
    return 0;
  }
  // do not add the quantity again: the expectation is to be able to see what remains, including what has already been added
  return (em.amount ?? 0) - (em.amountRequested ?? 0); //+ (quantity ?? 0);
}
